import axios from 'axios';
import type { MomentObject } from 'moment';
import LocalStorageService from './localStorageService';
import QueryString from '../utils/queryString';
import makeCancelable from '../utils/makeCancelable';
import toDateStringArray from '../utils/dates/toDateStringArray';
import toUtcDate from '../utils/dates/toUtcDate';
import { DAY_TYPE_FULL, DAY_TYPE_SPECIFIC_TIME } from '../constants';
import type { RequestSearchCriteria, SelectedLocation } from '../types';
import toISODateString from '../utils/dates/toISODateString';
import type { Clocking } from '../components/ReviewClockings/DaySummary';

function parseModelStateErrors(data) {
  const { errors } = data;
  let results = [];

  if (!errors) {
    return ['An unexpected error has occurred'];
  }

  Object.keys(errors).forEach(key => (results = [...results, ...errors[key]]));

  return results;
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

instance.defaults.headers.common.Accept = 'application/json';
instance.defaults.headers.common['Content-Type'] = 'application/json';

// https://github.com/axios/axios/issues/1383
instance.interceptors.request.use(
  function(config) {
    const token = LocalStorageService.getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  function(error) {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  function(response) {
    return response;
  },
  function(error) {
    if (error.isCanceled) {
      return Promise.reject(error);
    }

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      if (error.response.status === 401) {
        if (
          error.response.headers['www-authenticate'] &&
          error.response.headers['www-authenticate'].includes(
            'Bearer error="invalid_token"'
          )
        ) {
          LocalStorageService.removeToken();
          window.location.href = `${window.origin}/sessionExpired`;
          return Promise.reject(['Session expired']);
        }

        return Promise.reject([
          'You do not have permission to access the requested resource.'
        ]);
      }
      return Promise.reject(parseModelStateErrors(error.response.data));
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      return Promise.reject([
        'The server could not be reached, please try again later.'
      ]);
    } else {
      // Something happened in setting up the request that triggered an Error
      return Promise.reject(['Invalid request']);
    }
  }
);

const clientApi = {
  auth: {
    login(userName: string, password: string) {
      return instance.post('login', { userName, password });
    },
    validateToken() {
      return instance.get('tokens/validate');
    },
    passwordPolicy() {
      return instance.get('password/policy');
    },
    updatePassword(oldPassword: string, newPassword: string) {
      return instance.post('password', { oldPassword, newPassword });
    },
    resetPassword(emailAddress: string) {
      return instance.post('password/reset', { emailAddress });
    }
  },
  health: {
    get() {
      return axios.get(process.env.REACT_APP_HEALTH_URL);
    }
  },
  core: {
    getCostCentres() {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get('costCentres', {
          cancelToken: source.token
        }),
        source
      );
    },
    getPayCategories() {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get('payCategories?excludeLunchBreak=true', {
          cancelToken: source.token
        }),
        source
      );
    }
  },
  admin: {
    getListOfApprovers() {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get('approvers', {
          cancelToken: source.token
        }),
        source
      );
    },
    search(criteria: RequestSearchCriteria) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get('search/requests', {
          cancelToken: source.token,
          params: {
            ...criteria
          }
        }),
        source
      );
    }
  },
  company: {
    get() {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get('company', {
          cancelToken: source.token
        }),
        source
      );
    }
  },
  calendar: {
    getCalendarModifiers(
      clockNumber: number,
      yearFrom: number,
      yearTo: number,
      checkHistoricalFlag: boolean = false
    ) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `employees/${clockNumber}/calendar/${yearFrom}/${yearTo}?checkHistoricalFlag=${checkHistoricalFlag}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getDepartmentCalendar(
      clockNumber: number,
      date: MomentObject,
      includeEverybodyInCostCentre: boolean = false
    ) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `employees/${clockNumber}/departmentCalendar/${date.year()}/${date.month() +
            1}/${date.date()}?includeEverybodyInCostCentre=${includeEverybodyInCostCentre}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getAvailabilityCalendarCalendar(
      date: MomentObject,
      searchCriteria: string
    ) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `availabilityCalendar/${date.year()}/${date.month() +
            1}/${date.date()}${searchCriteria}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getLegend() {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get('legend', {
          cancelToken: source.token
        }),
        source
      );
    }
  },
  employee: {
    get(clockNumber: number) {
      return instance.get(`employees/${clockNumber}`);
    },
    getAllowances(clockNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`employees/${clockNumber}/allowances`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getHistory(clockNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`employees/${clockNumber}/requests/history`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getContractedHours(clockNumber: number, date: MomentObject) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`employees/${clockNumber}/workPeriods/${date.valueOf()}`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getContractedMinutes(
      clockNumber: number,
      dates: Array<MomentObject>,
      startHalfDay: boolean,
      endHalfDay: boolean
    ) {
      const source = axios.CancelToken.source();

      const params = {
        dates: toDateStringArray(dates),
        startHalfDay,
        endHalfDay
      };

      return makeCancelable(
        instance.get(`employees/${clockNumber}/contractedMinutes`, {
          cancelToken: source.token,
          paramsSerializer: p => QueryString.stringify(p),
          params
        }),
        source
      );
    },
    getWeeklySummary(clockNumber: number, date: MomentObject) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `employees/${clockNumber}/weeklySummary?date=${toISODateString(
            date
          )}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getClockings(clockNumber: number, date: MomentObject) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `employees/${clockNumber}/clockings/${toISODateString(date)}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getFlexitimeSummary(clockNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`employees/${clockNumber}/flexitimeSummary`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getJobs(clockNumber: number, effectiveDate: MomentObject) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `employees/${clockNumber}/jobs/${toISODateString(effectiveDate)}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getJobSequences(clockNumber: number, jobNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`employees/${clockNumber}/jobs/${jobNumber}/sequences`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getClockedInStatusAndLocations(clockNumber: number) {
      const source = axios.CancelToken.source();

      const clockedInStatus = instance.get(`employees/${clockNumber}/status`, {
        cancelToken: source.token
      });
      const locations = instance.get(`employees/${clockNumber}/locations`, {
        cancelToken: source.token
      });

      return makeCancelable(axios.all([clockedInStatus, locations]), source);
    },
    submitWebClocking(
      clockNumber: number,
      action: string,
      description: string,
      location: {
        id: number,
        latitude: number,
        longitude: number,
        offSite: number
      },
      job: { number: number, sequence: number }
    ) {
      return instance.post(`employees/${clockNumber}/webClockings`, {
        clockNumber,
        action,
        location,
        description,
        job
      });
    },
    submitClockings(
      clockNumber: number,
      dated: string,
      clockings: Array<Clocking>,
      notes: string
    ) {
      return instance.post(`employees/${clockNumber}/clockings`, {
        clockNumber,
        dated,
        clockings,
        notes
      });
    },
    submitNewRequest(
      clockNumber: number,
      absenceTypeCode: string,
      startDate: MomentObject,
      startDateType: number,
      endDate: MomentObject,
      endDateType: number,
      notes: string
    ) {
      return instance.post(`employees/${clockNumber}/requests`, {
        absenceTypeCode,
        startDate: toUtcDate(startDate),
        startDateType:
          startDateType === DAY_TYPE_SPECIFIC_TIME
            ? DAY_TYPE_FULL
            : startDateType,
        endDate: toUtcDate(endDate),
        endDateType,
        notes
      });
    },
    getRequestState(requestId: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`requests/${requestId}/state`, {
          cancelToken: source.token
        }),
        source
      );
    },
    cancelRequest(requestId: number) {
      return instance.post(`requests/${requestId}/cancel`);
    },
    getProfile(clockNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`employees/${clockNumber}/profile`, {
          cancelToken: source.token
        }),
        source
      );
    },
    updateProfile(
      clockNumber: number,
      emailAddress: string,
      mobileTelephone: ?string
    ) {
      return instance.put(`employees/${clockNumber}/profile`, {
        emailAddress,
        mobileTelephone
      });
    },
    updateProfilePicture(
      clockNumber: number,
      data: any,
      updateProgressCallback: any => mixed
    ) {
      return instance.post(`employees/${clockNumber}/profile/picture`, data, {
        onUploadProgress: updateProgressCallback
      });
    }
  },
  approver: {
    getPendingRequests(clockNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`approvers/${clockNumber}/requests`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getRequest(requestId: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`requests/${requestId}`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getRequestAndDeclinedReasons(requestId) {
      const source = axios.CancelToken.source();

      const request = instance.get(`requests/${requestId}`, {
        cancelToken: source.token
      });
      const declinedReasons = instance.get('declinedReasons', {
        cancelToken: source.token
      });

      return makeCancelable(axios.all([request, declinedReasons]), source);
    },
    getNumberOfPendingAbsenceRequests(clockNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`approvers/${clockNumber}/requests/count`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getNumberOfPendingClockingRequests(clockNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`approvers/${clockNumber}/clockingRequests/count`, {
          cancelToken: source.token
        }),
        source
      );
    },
    approveRequest(requestId: number) {
      return instance.post(`requests/${requestId}/approve`);
    },
    declineRequest(requestId: number, declinedReasonId: number, notes: string) {
      return instance.post(`requests/${requestId}/decline`, {
        requestId,
        reasonId: declinedReasonId,
        notes
      });
    },
    approveCancellation(requestId: number) {
      return instance.post(`requests/${requestId}/approveCancellation`);
    },
    declineCancellation(requestId: number, notes: string) {
      return instance.post(`requests/${requestId}/declineCancellation`, {
        requestId,
        notes
      });
    },
    getOutstandingClockingRequests(
      clockNumber: number,
      searchCriteria: string
    ) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `approvers/${clockNumber}/clockingRequests${searchCriteria}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getApprovedClockingRequests(clockNumber: number, searchCriteria: string) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `approvers/${clockNumber}/clockingRequests/approved${searchCriteria}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getDeclinedClockingRequests(clockNumber: number, searchCriteria: string) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(
          `approvers/${clockNumber}/clockingRequests/declined${searchCriteria}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    },
    getClockingRequest(id: string) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`clockings/${id}`, {
          cancelToken: source.token
        }),
        source
      );
    },
    approveClockingRequest(
      id: number,
      notes: string,
      clockings: Array<Clocking>
    ) {
      return instance.post(`clockings/${id}/approve`, { notes, clockings });
    },
    declineClockingRequest(id: number, notes: string) {
      return instance.post(`clockings/${id}/decline`, { notes });
    }
  },
  reports: {
    getEmployeeSummary(clockNumber: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`reports/employeeSummary?clockNumber=${clockNumber}`, {
          cancelToken: source.token
        }),
        source
      );
    }
  },
  charts: {
    getRequestedDaysByRequestIdChartData(requestId: number) {
      const source = axios.CancelToken.source();

      return makeCancelable(
        instance.get(`charts/daySummaryByRequestId/${requestId}`, {
          cancelToken: source.token
        }),
        source
      );
    },
    getRequestedDaysByDateRangeChartData(
      clockNumber: number,
      startDate: MomentObject,
      endDate: MomentObject
    ) {
      const source = axios.CancelToken.source();

      const actualEndDate = endDate || startDate;

      return makeCancelable(
        instance.get(
          `charts/daySummaryByDateRange/${clockNumber}?startDate=${startDate.valueOf()}&endDate=${actualEndDate.valueOf()}`,
          {
            cancelToken: source.token
          }
        ),
        source
      );
    }
  }
};

export default clientApi;
